import React, { type ComponentType, type PropsWithChildren } from 'react';

import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import type { Extras, Scope } from '@sentry/types';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';

import { getIdentityEnv } from '@atlassian/atlassian-studio-env-utils';

export type SentryErrorBoundaryProps = {
	accountId?: string;
	fallbackComponent: ComponentType<FallbackProps>;
	/**
	 * Indicates the name of where log is coming from, first layer to differentiate where logging is coming from
	 */
	loggerName?: string;
	onError?: (error: Error) => void;
	/**
	 * Indicates the team name of where log is coming from
	 * This team must exist in Sentry https://atlassian-2y.sentry.io/settings/teams/
	 * Used to correctly assign sentry issues to the correct team
	 */
	teamName?: string | 'rovo-agents-fe';
	/**
	 * Indicates the touchpointId of where log is coming from, this is the second layer to differentiate where logging is coming from
	 * used for reusable components that are using same `loggerName` but different `touchpointId`
	 */
	touchpointId?: string;
};

export const setupSentryClient = ({
	packageName,
	sentryDsn,
}: {
	packageName?: string;
	sentryDsn?: string;
} = {}) => {
	const accountId = 'UNKNOWN_AAID'; // TODO: Mock AAID for when app is fetching accountId or if accountId is otherwise not available
	const product = 'atlassianStudio';
	const defaultLogger = 'logger.default';

	const environment = window.location.href.includes('localhost')
		? 'dev'
		: getIdentityEnv() === 'prod'
			? 'production'
			: getIdentityEnv();
	const defaultSentryDsn =
		'https://ae44830611a6df6a7da952dd70a22f95@o55978.ingest.us.sentry.io/4509031091929088';

	Sentry.init({
		dsn: sentryDsn ?? defaultSentryDsn,
		environment: environment,
		beforeSend: (event) => {
			// We only want errors captured explicitly via logException/logMessage or SentryErrorBoundary, to avoid unwanted errors being sent
			if (event.tags?.name === undefined) {
				return null;
			}
			return event;
		},
		integrations: [new Integrations.ExtraErrorData()],
		initialScope: {
			user: {
				id: accountId,
			},
			tags: {
				packageName: packageName ?? '@atlassian/atlassian-studio',
				product,
			},
		},
		attachStacktrace: true,
		// https://github.com/getsentry/sentry-javascript/issues/2292
		ignoreErrors: ['Non-Error exception captured'],
	});

	Sentry.setTag('product', product);
	Sentry.setTag('logger', defaultLogger);
	Sentry.setUser({ id: accountId });
};

type ExtrasAndTags = Extras & {
	accountId?: string;
	loggerName?: string;
	teamName?: string;
	touchpointId?: string;
	traceId?: string;
};

const setTagsFromExtras = (scope: Scope, extrasAndTags: ExtrasAndTags) => {
	scope.setTag('name', 'studio');

	if (extrasAndTags.loggerName) {
		scope.setTag('logger', `logger.${extrasAndTags.loggerName}`);
	}
	if (extrasAndTags.accountId) {
		scope.setUser({ id: extrasAndTags.accountId as string });
	}
	if (extrasAndTags.teamName) {
		scope.setTag('teamName', extrasAndTags.teamName as string);
	}
	if (extrasAndTags.touchpointId) {
		scope.setTag('touchpointId', extrasAndTags.touchpointId as string);
	}
	if (extrasAndTags.traceId) {
		scope.setTag('traceId', extrasAndTags.traceId as string);
	}
};

export const logException = (exception: Error | unknown, extrasAndTags?: ExtrasAndTags) => {
	Sentry.withScope((scope) => {
		scope.setTag('name', 'studio');

		if (extrasAndTags) {
			scope.setExtras(extrasAndTags);
			// @ts-ignore - TS2345 - Added during confluence root integration
			setTagsFromExtras(scope, extrasAndTags);
		}
		Sentry.captureException(exception, scope);
	});
};

export const logMessage = (msg: string, extrasAndTags?: ExtrasAndTags) => {
	Sentry.withScope((scope) => {
		scope.setTag('name', 'studio');

		if (extrasAndTags) {
			scope.setExtras(extrasAndTags);
			// @ts-ignore - TS2345 - Added during confluence root integration
			setTagsFromExtras(scope, extrasAndTags);
		}
		Sentry.captureMessage(msg, scope);
	});
};

export const SentryErrorBoundary = ({
	children,
	fallbackComponent,
	loggerName,
	accountId,
	onError,
	teamName,
	touchpointId,
}: PropsWithChildren<SentryErrorBoundaryProps>) => {
	return (
		<ErrorBoundary
			FallbackComponent={fallbackComponent}
			onError={(err) => {
				logException(err, { loggerName, accountId, teamName, touchpointId });
				onError?.(err);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};
